import { initializeApp } from 'firebase/app';

import {
  initializeAuth,
  browserLocalPersistence,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithCustomToken,
  signOut,
  sendEmailVerification,
  sendPasswordResetEmail,
  onAuthStateChanged as firebaseOnAuthStateChanged,
  OAuthProvider,
  signInWithCredential,
  GoogleAuthProvider,
  signInWithPopup,
  browserPopupRedirectResolver,
} from 'firebase/auth';
import {
  getFirestore,
  initializeFirestore,
  memoryLocalCache,
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  deleteDoc,
  updateDoc,
  addDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
// firebase/messaging not imported at top level — ServiceWorkers unsupported in WKWebView, adds ~500KB to bundle
// getMessaging/getToken/onMessage are lazy-loaded below only if needed
import { getFunctions, httpsCallable } from 'firebase/functions';
import posthog from 'posthog-js';

// Your Firebase configuration
const firebaseConfig = {
  apiKey: "AIzaSyBtOWaL28pEQBdfOtGRwJPfIdHsUfmw6gE",
  authDomain: "receiptwise-prod.firebaseapp.com",
  projectId: "receiptwise-prod",
  storageBucket: "receiptwise-prod.firebasestorage.app",
  messagingSenderId: "825253621356",
  appId: "1:825253621356:web:b0760529383674411af249",
  // measurementId omitted — presence in config triggers Firebase Analytics auto-init which crashes in WKWebView
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// App Check is intentionally disabled.
// ReCaptchaEnterpriseProvider causes Firebase Auth SDK to attempt Enterprise reCAPTCHA
// for phone MFA enrollment, which conflicts with the RecaptchaVerifier and produces
// auth/invalid-app-credential. Re-enable only after registering domains in the
// reCAPTCHA Enterprise console AND enforcing App Check on non-Auth APIs.
// initializeAuth with explicit localStorage persistence — avoids IndexedDB hang in WKWebView (Capacitor iOS)
export const auth = initializeAuth(app, {
  persistence: browserLocalPersistence,
});

// Firestore: memory-only cache in Capacitor WKWebView — IndexedDB persistence throws
// "INTERNAL ASSERTION FAILED: Unexpected state" in WKWebView.
export const isNativeApp = window.location.protocol === 'capacitor:';
// In Capacitor WKWebView, IndexedDB persistence triggers "INTERNAL ASSERTION FAILED: Unexpected state".
// Memory-only cache bypasses IndexedDB entirely — data loads fresh from Firestore each session.
export const db = isNativeApp
  ? initializeFirestore(app, {
      localCache: memoryLocalCache(),
      // Force long-polling instead of WebChannel — WebChannel's streaming
      // connection triggers "INTERNAL ASSERTION FAILED" in WKWebView
      experimentalForceLongPolling: true,
    })
  : getFirestore(app);
const functions = getFunctions(app);

// Feature usage analytics — PostHog
posthog.init('phc_z5m4hcMENDej92unpyE2gP9yVRFxzbPkLgEmVQB8wk86', {
  api_host: 'https://us.i.posthog.com',
  person_profiles: 'identified_only',
});
export const logEvent = (eventName, params = {}) => {
  try { posthog.capture(eventName, params); } catch (_) {}
};

let messagingInstance = null;
const getMessagingInstance = async () => {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return null;
  try {
    if (!messagingInstance) {
      const { getMessaging } = await import('firebase/messaging');
      messagingInstance = getMessaging(app);
    }
    return messagingInstance;
  } catch (error) {
    console.warn('Messaging unavailable:', error?.message || error);
    return null;
  }
};

// ==================== AUTH FUNCTIONS ====================

export const signUp = async (email, password) => {
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    return { success: true, user: userCredential.user };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const resetPassword = async (email) => {
  try {
    await sendPasswordResetEmail(auth, email);
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// _restUser is kept for backwards compat with getRestUser() checks elsewhere in the app
let _restUser = null;
export const getRestUser = () => _restUser;

export const signIn = async (email, password) => {
  // Step 1: Try Firebase Auth SDK sign-in.
  // This is required for Firestore to receive proper auth tokens — Firestore subscribes
  // to Firebase Auth SDK's internal token events, not auth.currentUser directly.
  // Email/password sign-in does not require reCAPTCHA, so this works in WKWebView.
  try {
    const credential = await signInWithEmailAndPassword(auth, email, password);
    const fbUser = credential.user;
    _restUser = {
      uid: fbUser.uid,
      email: fbUser.email,
      emailVerified: fbUser.emailVerified,
      displayName: fbUser.displayName || '',
      name: fbUser.displayName || (fbUser.email || '').split('@')[0],
      photoURL: fbUser.photoURL,
      phoneNumber: fbUser.phoneNumber,
      getIdToken: (forceRefresh) => fbUser.getIdToken(forceRefresh),
    };
    return { success: true, user: _restUser };
  } catch (sdkError) {
    // Step 2: SDK failed (reCAPTCHA enforcement, domain restriction, etc.).
    // Fall back to REST API for auth, but Firestore reads will fail without SDK auth.
    console.warn('[CENTRIS] SDK sign-in failed, trying REST fallback:', sdkError.code, sdkError.message);

    // MFA required — native Capacitor path uses REST v2 to send SMS without reCAPTCHA
    if (sdkError.code === 'auth/multi-factor-auth-required') {
      if (isNativeApp) {
        // Step 1: Call REST v1 to get mfaPendingCredential and enrolled factor info
        try {
          const v1Resp = await fetch(
            `https://identitytoolkit.googleapis.com/v1/accounts:signInWithPassword?key=${firebaseConfig.apiKey}`,
            { method: 'POST', headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ email, password, returnSecureToken: true }) }
          );
          const v1Json = await v1Resp.json();
          // Firebase returns 400 with MFA details in error.details
          // Firebase can return MFA details in multiple locations depending on version
          const errDetails = v1Json?.error?.details || [];
          const mfaDetail = errDetails.find(d => d.mfaPendingCredential || (d['@type'] || '').includes('MfaEnrollment'));
          const mfaPendingCredential =
            mfaDetail?.mfaPendingCredential ||
            v1Json?.mfaPendingCredential ||
            v1Json?.error?.mfaPendingCredential;
          const mfaInfo = mfaDetail?.mfaInfo || v1Json?.mfaInfo || [];
          const mfaEnrollmentId = mfaInfo[0]?.mfaEnrollmentId || '';
          const phoneHint = mfaInfo[0]?.phoneInfo || 'your phone';
          if (!mfaPendingCredential) {
            return { success: false, code: 'auth/multi-factor-auth-required', error: 'MFA required — unable to extract pending credential.', rawError: sdkError };
          }
          // Step 2: Call Cloud Function to send SMS (bypasses browser reCAPTCHA)
          const smsResp = await fetch(
            'https://us-central1-receiptwise-prod.cloudfunctions.net/sendMfaSms',
            { method: 'POST', headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ mfaPendingCredential, mfaEnrollmentId }) }
          );
          const smsJson = await smsResp.json();
          if (!smsResp.ok || !smsJson.sessionInfo) {
            console.error('[CENTRIS] sendMfaSms failed:', smsJson);
            return { success: false, code: 'auth/multi-factor-auth-required', error: smsJson.error || 'Failed to send verification SMS.', rawError: sdkError };
          }
          return {
            success: false,
            code: 'auth/native-mfa-required',
            sessionInfo: smsJson.sessionInfo,
            mfaPendingCredential,
            phoneHint,
          };
        } catch (nativeMfaErr) {
          console.error('[CENTRIS] Native MFA start error:', nativeMfaErr);
          return { success: false, code: 'auth/multi-factor-auth-required', error: nativeMfaErr.message, rawError: sdkError };
        }
      }
      // Web path — pass rawError so handleAuth calls startMfaChallenge with reCAPTCHA
      return { success: false, code: 'auth/multi-factor-auth-required', error: sdkError.message, rawError: sdkError };
    }

    // Credential/account errors — return immediately, no REST fallback needed
    const terminalCodes = new Set([
      'auth/user-not-found', 'auth/wrong-password', 'auth/invalid-credential',
      'auth/user-disabled', 'auth/too-many-requests', 'auth/invalid-email',
    ]);
    if (terminalCodes.has(sdkError.code)) {
      return { success: false, error: sdkError.message, code: sdkError.code };
    }
  }

  // REST API fallback — use when SDK is blocked by reCAPTCHA/App Check enforcement
  try {
    const resp = await fetch(
      `https://identitytoolkit.googleapis.com/v1/accounts:signInWithPassword?key=${firebaseConfig.apiKey}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, returnSecureToken: true }),
      }
    );
    const json = await resp.json();
    if (!resp.ok) {
      const msg = json.error?.message || 'UNKNOWN';
      const codeMap = {
        INVALID_LOGIN_CREDENTIALS: 'auth/invalid-credential',
        EMAIL_NOT_FOUND: 'auth/user-not-found',
        INVALID_PASSWORD: 'auth/wrong-password',
        USER_DISABLED: 'auth/user-disabled',
        TOO_MANY_ATTEMPTS_TRY_LATER: 'auth/too-many-requests',
      };
      const code = codeMap[msg] || 'auth/invalid-credential';
      return { success: false, error: `Firebase: Error (${code}).`, code };
    }
    let _token = json.idToken;
    let _tokenExpiry = Date.now() + parseInt(json.expiresIn || '3600') * 1000;
    const _refreshToken = json.refreshToken;
    const refreshIfNeeded = async () => {
      if (Date.now() < _tokenExpiry - 60000) return _token;
      const r = await fetch(
        `https://securetoken.googleapis.com/v1/token?key=${firebaseConfig.apiKey}`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ grant_type: 'refresh_token', refresh_token: _refreshToken }) }
      );
      const t = await r.json();
      _token = t.id_token; _tokenExpiry = Date.now() + parseInt(t.expires_in || '3600') * 1000;
      return _token;
    };
    const displayName = json.displayName || '';
    _restUser = {
      uid: json.localId, email: json.email, emailVerified: json.registered || false,
      displayName, name: displayName || json.email.split('@')[0],
      photoURL: null, phoneNumber: null, getIdToken: refreshIfNeeded,
    };
    try { Object.defineProperty(auth, 'currentUser', { get: () => _restUser, configurable: true }); } catch (_) {}
    return { success: true, user: _restUser };
  } catch (error) {
    return { success: false, error: error.message, code: 'auth/internal-error' };
  }
};

export const logOut = async () => {
  try {
    _restUser = null; // clear so onAuthStateChanged guard doesn't block post-logout state
    await signOut(auth);
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Complete native (Capacitor) MFA sign-in without browser reCAPTCHA.
// Flow: REST v2 finalize → idToken → mintCustomToken CF → signInWithCustomToken SDK
export const completeNativeMfa = async (sessionInfo, mfaPendingCredential, otpCode) => {
  try {
    // Step 1: Finalize MFA via REST v2 — no reCAPTCHA required at this step
    const finalizeResp = await fetch(
      `https://identitytoolkit.googleapis.com/v2/accounts/mfaSignIn:finalize?key=${firebaseConfig.apiKey}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mfaPendingCredential,
          phoneVerificationInfo: { sessionInfo, code: otpCode },
        }),
      }
    );
    const finalizeJson = await finalizeResp.json();
    if (!finalizeResp.ok) {
      const msg = finalizeJson?.error?.message || 'OTP verification failed.';
      const codeMap = {
        INVALID_CODE: 'Invalid code — please check and try again.',
        CODE_EXPIRED: 'Code has expired. Please go back and sign in again.',
        SESSION_EXPIRED: 'Session expired. Please go back and sign in again.',
        MISSING_CODE: 'Please enter the 6-digit code from your SMS.',
      };
      return { success: false, error: codeMap[msg] || msg };
    }
    const idToken = finalizeJson.idToken;
    if (!idToken) return { success: false, error: 'Sign-in failed — no token returned.' };

    // Step 2: Exchange Firebase MFA idToken for a custom token via Cloud Function
    const mintResp = await fetch(
      'https://us-central1-receiptwise-prod.cloudfunctions.net/mintCustomToken',
      { method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ idToken }) }
    );
    const mintJson = await mintResp.json();
    if (!mintResp.ok || !mintJson.customToken) {
      return { success: false, error: mintJson.error || 'Token exchange failed.' };
    }

    // Step 3: Sign in with custom token so Firebase SDK (and Firestore) gets proper auth
    const credential = await signInWithCustomToken(auth, mintJson.customToken);
    // Reload to fetch latest user data (email verified status, enrolled MFA factors, etc.)
    await credential.user.reload();
    const fbUser = auth.currentUser;
    _restUser = {
      uid: fbUser.uid,
      email: fbUser.email,
      emailVerified: fbUser.emailVerified,
      displayName: fbUser.displayName || '',
      name: fbUser.displayName || (fbUser.email || '').split('@')[0],
      photoURL: fbUser.photoURL,
      phoneNumber: fbUser.phoneNumber,
      getIdToken: (forceRefresh) => fbUser.getIdToken(forceRefresh),
    };
    return { success: true, user: _restUser };
  } catch (error) {
    console.error('[CENTRIS] completeNativeMfa error:', error);
    return { success: false, error: error.message || 'MFA sign-in failed.' };
  }
};

export const onAuthStateChanged = (callback) => {
  return firebaseOnAuthStateChanged(auth, callback);
};

// ==================== EXPENSE FUNCTIONS ====================

export const saveExpense = async (userId, expenseData) => {
  try {
    const expenseId = Date.now().toString();
    const expenseRef = doc(db, 'users', userId, 'expenses', expenseId);
    
    await setDoc(expenseRef, {
      ...expenseData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    
    return { success: true, id: expenseId };
  } catch (error) {
    console.error('Error saving expense:', error);
    return { success: false, error: error.message };
  }
};

export const loadExpenses = async (userId) => {
  try {
    const expensesRef = collection(db, 'users', userId, 'expenses');
    const q = query(expensesRef, orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);
    
    const expenses = [];
    querySnapshot.forEach((docSnap) => {
      expenses.push({ id: docSnap.id, ...docSnap.data() });
    });
    
    return { success: true, expenses };
  } catch (error) {
    console.error('Error loading expenses:', error);
    return { success: false, error: error.message, expenses: [] };
  }
};

export const deleteExpense = async (userId, expenseId) => {
  try {
    const expenseRef = doc(db, 'users', userId, 'expenses', expenseId);
    await deleteDoc(expenseRef);
    return { success: true };
  } catch (error) {
    console.error('Error deleting expense:', error);
    return { success: false, error: error.message };
  }
};

export const deleteAllExpenses = async (userId) => {
  try {
    const expensesRef = collection(db, 'users', userId, 'expenses');
    const querySnapshot = await getDocs(expensesRef);
    
    const deletePromises = [];
    querySnapshot.forEach((docSnap) => {
      deletePromises.push(deleteDoc(docSnap.ref));
    });
    
    await Promise.all(deletePromises);
    return { success: true, count: deletePromises.length };
  } catch (error) {
    console.error('Error deleting all expenses:', error);
    return { success: false, error: error.message };
  }
};

export const checkDuplicateExpense = async (userId, expenseData) => {
  try {
    const expensesRef = collection(db, 'users', userId, 'expenses');
    const querySnapshot = await getDocs(expensesRef);
    
    let isDuplicate = false;
    querySnapshot.forEach((docSnap) => {
      const existing = docSnap.data();
      if (
        existing.merchant === expenseData.merchant &&
        Math.abs(existing.amount - expenseData.amount) < 0.01 &&
        existing.date === expenseData.date
      ) {
        isDuplicate = true;
      }
    });
    
    return { isDuplicate };
  } catch (error) {
    console.error('Error checking duplicate:', error);
    return { isDuplicate: false };
  }
};

// ==================== INVOICE FUNCTIONS ====================

export const saveInvoice = async (userId, invoiceData) => {
  try {
    const invoiceId = invoiceData.id || Date.now().toString();
    const invoiceRef = doc(db, 'users', userId, 'invoices', invoiceId);
    
    await setDoc(invoiceRef, {
      ...invoiceData,
      createdAt: invoiceData.createdAt || serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    
    return { success: true, id: invoiceId };
  } catch (error) {
    console.error('Error saving invoice:', error);
    return { success: false, error: error.message };
  }
};

export const loadInvoices = async (userId) => {
  try {
    const invoicesRef = collection(db, 'users', userId, 'invoices');
    const q = query(invoicesRef, orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);
    
    const invoices = [];
    querySnapshot.forEach((docSnap) => {
      invoices.push({ id: docSnap.id, ...docSnap.data() });
    });
    
    return { success: true, invoices };
  } catch (error) {
    console.error('Error loading invoices:', error);
    return { success: false, error: error.message, invoices: [] };
  }
};

export const updateInvoice = async (userId, invoiceId, updates) => {
  try {
    const invoiceRef = doc(db, 'users', userId, 'invoices', invoiceId);
    await updateDoc(invoiceRef, {
      ...updates,
      updatedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    console.error('Error updating invoice:', error);
    return { success: false, error: error.message };
  }
};

export const deleteInvoice = async (userId, invoiceId) => {
  try {
    const invoiceRef = doc(db, 'users', userId, 'invoices', invoiceId);
    await deleteDoc(invoiceRef);
    return { success: true };
  } catch (error) {
    console.error('Error deleting invoice:', error);
    return { success: false, error: error.message };
  }
};

// ==================== MILEAGE FUNCTIONS ====================

export const saveMileageTrip = async (userId, tripData) => {
  try {
    const tripId = tripData.id || Date.now().toString();
    const tripRef = doc(db, 'users', userId, 'mileageTrips', tripId);
    
    await setDoc(tripRef, {
      ...tripData,
      createdAt: tripData.createdAt || serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    
    return { success: true, id: tripId };
  } catch (error) {
    console.error('Error saving trip:', error);
    return { success: false, error: error.message };
  }
};

export const loadMileageTrips = async (userId) => {
  try {
    const tripsRef = collection(db, 'users', userId, 'mileageTrips');
    const q = query(tripsRef, orderBy('date', 'desc'));
    const querySnapshot = await getDocs(q);
    
    const trips = [];
    querySnapshot.forEach((docSnap) => {
      trips.push({ id: docSnap.id, ...docSnap.data() });
    });
    
    return { success: true, trips };
  } catch (error) {
    console.error('Error loading trips:', error);
    return { success: false, error: error.message, trips: [] };
  }
};

export const deleteMileageTrip = async (userId, tripId) => {
  try {
    const tripRef = doc(db, 'users', userId, 'mileageTrips', tripId);
    await deleteDoc(tripRef);
    return { success: true };
  } catch (error) {
    console.error('Error deleting trip:', error);
    return { success: false, error: error.message };
  }
};

// ==================== SUBSCRIPTION FUNCTIONS ====================

export const saveSubscription = async (userId, subscriptionData) => {
  try {
    const subId = subscriptionData.id || Date.now().toString();
    const subRef = doc(db, 'users', userId, 'subscriptions', subId);
    
    await setDoc(subRef, {
      ...subscriptionData,
      updatedAt: serverTimestamp()
    });
    
    return { success: true, id: subId };
  } catch (error) {
    console.error('Error saving subscription:', error);
    return { success: false, error: error.message };
  }
};

export const loadSubscriptions = async (userId) => {
  try {
    const subsRef = collection(db, 'users', userId, 'subscriptions');
    const querySnapshot = await getDocs(subsRef);
    
    const subscriptions = [];
    querySnapshot.forEach((docSnap) => {
      subscriptions.push({ id: docSnap.id, ...docSnap.data() });
    });
    
    return { success: true, subscriptions };
  } catch (error) {
    console.error('Error loading subscriptions:', error);
    return { success: false, error: error.message, subscriptions: [] };
  }
};

export const deleteSubscription = async (userId, subId) => {
  try {
    const subRef = doc(db, 'users', userId, 'subscriptions', subId);
    await deleteDoc(subRef);
    return { success: true };
  } catch (error) {
    console.error('Error deleting subscription:', error);
    return { success: false, error: error.message };
  }
};

export const updateSubscription = async (userId, subId, updates) => {
  try {
    const subRef = doc(db, 'users', userId, 'subscriptions', subId);
    await updateDoc(subRef, {
      ...updates,
      updatedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    console.error('Error updating subscription:', error);
    return { success: false, error: error.message };
  }
};

// ==================== REVENUE FUNCTIONS ====================

const normalizeDate = (value) => {
  if (!value) return new Date().toISOString().split('T')[0];
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value).split('T')[0];
  return d.toISOString().split('T')[0];
};

export const saveRevenue = async (userId, revenueData) => {
  try {
    const revenueId = revenueData.id || Date.now().toString();
    const revenueRef = doc(db, 'users', userId, 'revenue', revenueId);

    await setDoc(revenueRef, {
      ...revenueData,
      amount: Number(revenueData.amount || 0),
      date: normalizeDate(revenueData.date),
      createdAt: revenueData.createdAt || serverTimestamp(),
      updatedAt: serverTimestamp()
    }, { merge: true });

    return { success: true, id: revenueId };
  } catch (error) {
    console.error('Error saving revenue:', error);
    return { success: false, error: error.message };
  }
};

export const loadRevenue = async (userId) => {
  try {
    const revenueRef = collection(db, 'users', userId, 'revenue');
    const querySnapshot = await getDocs(revenueRef);

    const revenue = [];
    querySnapshot.forEach((docSnap) => {
      revenue.push({ id: docSnap.id, ...docSnap.data() });
    });

    revenue.sort((a, b) => {
      const ad = new Date(a.date || 0).getTime();
      const bd = new Date(b.date || 0).getTime();
      return bd - ad;
    });

    return { success: true, revenue };
  } catch (error) {
    console.error('Error loading revenue:', error);
    return { success: false, error: error.message, revenue: [] };
  }
};

export const deleteRevenue = async (userId, revenueId) => {
  try {
    const revenueRef = doc(db, 'users', userId, 'revenue', revenueId);
    await deleteDoc(revenueRef);
    return { success: true };
  } catch (error) {
    console.error('Error deleting revenue:', error);
    return { success: false, error: error.message };
  }
};

export const checkDuplicateRevenue = async (userId, revenueData, invoices = []) => {
  try {
    const amount = Number(revenueData.amount || 0);
    const date = normalizeDate(revenueData.date);
    const source = String(revenueData.source || '').toLowerCase().trim();

    const revenueResult = await loadRevenue(userId);
    const existing = revenueResult.success ? revenueResult.revenue : [];

    const inRevenue = existing.some((r) => {
      const ra = Number(r.amount || 0);
      const rd = normalizeDate(r.date);
      const rs = String(r.source || '').toLowerCase().trim();
      return Math.abs(ra - amount) < 0.01 && rd === date && rs === source;
    });

    const inInvoices = (invoices || []).some((inv) => {
      if (inv.status && inv.status !== 'paid') return false;
      const ia = Number(inv.total || 0);
      const idate = normalizeDate(inv.date || inv.paidDate);
      return Math.abs(ia - amount) < 0.01 && idate === date;
    });

    return { isDuplicate: inRevenue || inInvoices, inRevenue, inInvoices };
  } catch (error) {
    console.error('Error checking duplicate revenue:', error);
    return { isDuplicate: false, inRevenue: false, inInvoices: false };
  }
};

// ==================== BUDGET FUNCTIONS ====================

export const saveBudget = async (userId, budgetData) => {
  try {
    const budgetId = budgetData.id || Date.now().toString();
    const budgetRef = doc(db, 'users', userId, 'budgets', budgetId);
    await setDoc(budgetRef, {
      ...budgetData,
      amount: Number(budgetData.amount || 0),
      updatedAt: serverTimestamp()
    }, { merge: true });
    return { success: true, id: budgetId };
  } catch (error) {
    console.error('Error saving budget:', error);
    return { success: false, error: error.message };
  }
};

export const loadBudgets = async (userId) => {
  try {
    const budgetsRef = collection(db, 'users', userId, 'budgets');
    const querySnapshot = await getDocs(budgetsRef);
    const budgets = [];
    querySnapshot.forEach((docSnap) => budgets.push({ id: docSnap.id, ...docSnap.data() }));
    return { success: true, budgets };
  } catch (error) {
    console.error('Error loading budgets:', error);
    return { success: false, error: error.message, budgets: [] };
  }
};

export const deleteBudget = async (userId, budgetId) => {
  try {
    await deleteDoc(doc(db, 'users', userId, 'budgets', budgetId));
    return { success: true };
  } catch (error) {
    console.error('Error deleting budget:', error);
    return { success: false, error: error.message };
  }
};

// ==================== BILL FUNCTIONS ====================

const addByFrequency = (dateStr, frequency) => {
  const d = new Date(dateStr || new Date().toISOString().split('T')[0]);
  switch (frequency) {
    case 'weekly':
      d.setDate(d.getDate() + 7);
      break;
    case 'biweekly':
      d.setDate(d.getDate() + 14);
      break;
    case 'quarterly':
      d.setMonth(d.getMonth() + 3);
      break;
    case 'yearly':
    case 'annual':
      d.setFullYear(d.getFullYear() + 1);
      break;
    default:
      d.setMonth(d.getMonth() + 1);
      break;
  }
  return d.toISOString().split('T')[0];
};

export const saveBill = async (userId, billData) => {
  try {
    const billId = billData.id || Date.now().toString();
    const billRef = doc(db, 'users', userId, 'bills', billId);
    await setDoc(billRef, {
      ...billData,
      amount: Number(billData.amount || 0),
      nextDueDate: normalizeDate(billData.nextDueDate),
      updatedAt: serverTimestamp()
    }, { merge: true });
    return { success: true, id: billId };
  } catch (error) {
    console.error('Error saving bill:', error);
    return { success: false, error: error.message };
  }
};

export const loadBills = async (userId) => {
  try {
    const billsRef = collection(db, 'users', userId, 'bills');
    const querySnapshot = await getDocs(billsRef);
    const bills = [];
    querySnapshot.forEach((docSnap) => bills.push({ id: docSnap.id, ...docSnap.data() }));
    bills.sort((a, b) => String(a.nextDueDate || '').localeCompare(String(b.nextDueDate || '')));
    return { success: true, bills };
  } catch (error) {
    console.error('Error loading bills:', error);
    return { success: false, error: error.message, bills: [] };
  }
};

export const markBillPaid = async (userId, billId, billData = {}) => {
  try {
    const nextDueDate = addByFrequency(billData.nextDueDate, billData.frequency);
    const billRef = doc(db, 'users', userId, 'bills', billId);
    await updateDoc(billRef, {
      lastPaidAt: serverTimestamp(),
      nextDueDate,
      updatedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    console.error('Error marking bill paid:', error);
    return { success: false, error: error.message };
  }
};

export const deleteBill = async (userId, billId) => {
  try {
    await deleteDoc(doc(db, 'users', userId, 'bills', billId));
    return { success: true };
  } catch (error) {
    console.error('Error deleting bill:', error);
    return { success: false, error: error.message };
  }
};

// ==================== SCHEDULED TX FUNCTIONS ====================

export const saveScheduledTx = async (userId, txData) => {
  try {
    const txId = txData.id || Date.now().toString();
    const txRef = doc(db, 'users', userId, 'scheduledTx', txId);
    await setDoc(txRef, {
      ...txData,
      amount: Number(txData.amount || 0),
      nextDate: normalizeDate(txData.nextDate),
      active: txData.active !== false,
      updatedAt: serverTimestamp()
    }, { merge: true });
    return { success: true, id: txId };
  } catch (error) {
    console.error('Error saving scheduled transaction:', error);
    return { success: false, error: error.message };
  }
};

export const loadScheduledTx = async (userId) => {
  try {
    const txRef = collection(db, 'users', userId, 'scheduledTx');
    const querySnapshot = await getDocs(txRef);
    const txs = [];
    querySnapshot.forEach((docSnap) => txs.push({ id: docSnap.id, ...docSnap.data() }));
    txs.sort((a, b) => String(a.nextDate || '').localeCompare(String(b.nextDate || '')));
    return { success: true, txs };
  } catch (error) {
    console.error('Error loading scheduled transactions:', error);
    return { success: false, error: error.message, txs: [] };
  }
};

export const deleteScheduledTx = async (userId, txId) => {
  try {
    await deleteDoc(doc(db, 'users', userId, 'scheduledTx', txId));
    return { success: true };
  } catch (error) {
    console.error('Error deleting scheduled transaction:', error);
    return { success: false, error: error.message };
  }
};

export const updateScheduledTx = async (userId, txId, updates) => {
  try {
    const txRef = doc(db, 'users', userId, 'scheduledTx', txId);
    await updateDoc(txRef, {
      ...updates,
      updatedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    console.error('Error updating scheduled transaction:', error);
    return { success: false, error: error.message };
  }
};

// ==================== SMART CATEGORY HELPERS ====================

const normalizeMerchant = (text = '') =>
  String(text)
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

export const findSimilarPatterns = async (merchantName, userId) => {
  try {
    const target = normalizeMerchant(merchantName);
    if (!target) return [];

    const result = await loadExpenses(userId);
    const expenses = result.success ? result.expenses : [];

    const matches = expenses
      .filter((e) => e.merchant && e.category)
      .map((e) => {
        const candidate = normalizeMerchant(e.merchant);
        const exact = candidate === target;
        const partial = candidate.includes(target) || target.includes(candidate);
        if (!exact && !partial) return null;
        return {
          pattern: { merchant: e.merchant, category: e.category },
          confidence: exact ? 95 : 78
        };
      })
      .filter(Boolean)
      .slice(0, 5);

    return matches;
  } catch (error) {
    console.error('Error finding similar patterns:', error);
    return [];
  }
};

export const predictCategory = async (merchantName, userId) => {
  try {
    const patterns = await findSimilarPatterns(merchantName, userId);
    if (patterns.length > 0) {
      return {
        category: patterns[0].pattern.category,
        confidence: patterns[0].confidence
      };
    }

    const name = normalizeMerchant(merchantName);
    const rules = [
      { re: /(uber|lyft|shell|chevron|exxon|bp|fuel|gas|parking|toll)/, category: 'Transportation' },
      { re: /(amazon|walmart|target|costco|kroger|aldi|safeway)/, category: 'Office Supplies' },
      { re: /(adobe|google|microsoft|github|aws|cloud|zoom|slack)/, category: 'Software' },
      { re: /(restaurant|coffee|cafe|doordash|grubhub|ubereats|chipotle)/, category: 'Meals & Entertainment' },
      { re: /(hotel|airbnb|airline|delta|united|southwest)/, category: 'Travel & Lodging' },
      { re: /(comcast|xfinity|verizon|att|utility|electric|water|internet)/, category: 'Utilities' },
    ];

    const matched = rules.find((r) => r.re.test(name));
    if (matched) return { category: matched.category, confidence: 72 };
    return { category: 'Other', confidence: 45 };
  } catch (error) {
    console.error('Error predicting category:', error);
    return { category: 'Other', confidence: 0 };
  }
};

// ==================== MARKETING HUB FUNCTIONS ====================

const MARKETING_USAGE_DEFAULTS = {
  scripts: 0,
  atomizer: 0,
  trends: 0,
  demographics: 0,
  pricing: 0,
  videos: 0,
  month: new Date().toISOString().slice(0, 7)
};

export const getMarketingUsage = async (userId) => {
  try {
    const usageRef = doc(db, 'users', userId, 'marketing', 'usage');
    const snap = await getDoc(usageRef);
    const month = new Date().toISOString().slice(0, 7);
    const data = snap.exists() ? snap.data() : {};
    const usage = { ...MARKETING_USAGE_DEFAULTS, ...data };
    if (usage.month !== month) {
      usage.scripts = 0;
      usage.atomizer = 0;
      usage.trends = 0;
      usage.demographics = 0;
      usage.pricing = 0;
      usage.videos = 0;
      usage.month = month;
      await setDoc(usageRef, usage, { merge: true });
    }
    return { success: true, usage };
  } catch (error) {
    console.error('Error loading marketing usage:', error);
    return { success: false, error: error.message, usage: { ...MARKETING_USAGE_DEFAULTS } };
  }
};

export const incrementMarketingUsage = async (userId, key) => {
  try {
    const usageResult = await getMarketingUsage(userId);
    const usage = usageResult.success ? usageResult.usage : { ...MARKETING_USAGE_DEFAULTS };
    usage[key] = Number(usage[key] || 0) + 1;
    const usageRef = doc(db, 'users', userId, 'marketing', 'usage');
    await setDoc(usageRef, { ...usage, updatedAt: serverTimestamp() }, { merge: true });
    return { success: true, usage };
  } catch (error) {
    console.error('Error incrementing marketing usage:', error);
    return { success: false, error: error.message };
  }
};

export const loadClientSources = async (userId) => {
  try {
    const sourceRef = collection(db, 'users', userId, 'clientSources');
    const querySnapshot = await getDocs(sourceRef);
    const sources = {};
    querySnapshot.forEach((docSnap) => {
      sources[docSnap.id] = docSnap.data();
    });
    return { success: true, sources };
  } catch (error) {
    console.error('Error loading client sources:', error);
    return { success: false, error: error.message, sources: {} };
  }
};

export const saveClientSource = async (userId, clientName, payload = {}) => {
  try {
    const id = String(clientName || '')
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]/g, '_');
    if (!id) return { success: false, error: 'Invalid client name' };

    const sourceRef = doc(db, 'users', userId, 'clientSources', id);
    await setDoc(sourceRef, {
      clientName,
      source: payload.source || 'other',
      detail: payload.detail || '',
      updatedAt: serverTimestamp()
    }, { merge: true });
    return { success: true, id };
  } catch (error) {
    console.error('Error saving client source:', error);
    return { success: false, error: error.message };
  }
};

export const saveContentDistributionBatch = async (userId, payload = {}) => {
  try {
    const platforms = Array.isArray(payload.platforms) ? payload.platforms.filter(Boolean) : [];
    const posts = Array.isArray(payload.posts) ? payload.posts.filter((p) => p && p.content) : [];
    if (!userId) return { success: false, error: 'Missing userId' };
    if (!platforms.length) return { success: false, error: 'Select at least one platform' };
    if (!posts.length) return { success: false, error: 'No content to distribute' };

    const scheduleAtDate = payload.scheduleAt ? new Date(payload.scheduleAt) : null;
    const isScheduled = scheduleAtDate && !Number.isNaN(scheduleAtDate.getTime());

    const jobsRef = collection(db, 'users', userId, 'contentDistributionJobs');
    const writes = [];

    for (const platform of platforms) {
      for (const post of posts) {
        writes.push(addDoc(jobsRef, {
          platform: String(platform),
          slot: String(post.slot || 'post'),
          content: String(post.content || ''),
          source: payload.source || 'content_atomizer',
          status: isScheduled ? 'scheduled' : 'queued',
          scheduleAt: isScheduled ? Timestamp.fromDate(scheduleAtDate) : null,
          publishedAt: null,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        }));
      }
    }

    await Promise.all(writes);
    return { success: true, createdCount: writes.length };
  } catch (error) {
    console.error('Error saving distribution jobs:', error);
    return { success: false, error: error.message };
  }
};

export const loadContentDistributionJobs = async (userId, maxRows = 40) => {
  try {
    if (!userId) return { success: true, jobs: [] };
    const jobsRef = collection(db, 'users', userId, 'contentDistributionJobs');
    const q = query(jobsRef, orderBy('createdAt', 'desc'));
    const snap = await getDocs(q);
    const jobs = [];
    snap.forEach((docSnap) => {
      jobs.push({ id: docSnap.id, ...docSnap.data() });
    });
    return { success: true, jobs: jobs.slice(0, Math.max(1, Number(maxRows) || 40)) };
  } catch (error) {
    console.error('Error loading distribution jobs:', error);
    return { success: false, error: error.message, jobs: [] };
  }
};

// ==================== TAX VAULT FUNCTIONS ====================

const DEFAULT_TAX_VAULT_SETTINGS = {
  reservePercent: 25,
  cushionPercent: 5,
  enabled: true,
  autoPullEnabled: false,
  targetDate: '',
  taxBracket: 25,
  taxVaultAccountId: null,
  taxVaultAccountName: null
};

export const loadTaxVaultSettings = async (userId) => {
  try {
    const settingsRef = doc(db, 'users', userId, 'taxVault', 'settings');
    const snap = await getDoc(settingsRef);
    if (!snap.exists()) {
      return { success: true, settings: DEFAULT_TAX_VAULT_SETTINGS };
    }
    return {
      success: true,
      settings: { ...DEFAULT_TAX_VAULT_SETTINGS, ...snap.data() }
    };
  } catch (error) {
    console.error('Error loading tax vault settings:', error);
    return { success: false, error: error.message, settings: DEFAULT_TAX_VAULT_SETTINGS };
  }
};

export const saveTaxVaultSettings = async (userId, settings) => {
  try {
    const settingsRef = doc(db, 'users', userId, 'taxVault', 'settings');
    await setDoc(settingsRef, {
      ...DEFAULT_TAX_VAULT_SETTINGS,
      ...settings,
      updatedAt: serverTimestamp()
    }, { merge: true });
    return { success: true };
  } catch (error) {
    console.error('Error saving tax vault settings:', error);
    return { success: false, error: error.message };
  }
};

export const loadTaxVaultTransactions = async (userId) => {
  try {
    const txRef = collection(db, 'users', userId, 'taxVaultTransactions');
    const q = query(txRef, orderBy('date', 'desc'));
    const querySnapshot = await getDocs(q);

    const transactions = [];
    querySnapshot.forEach((docSnap) => {
      transactions.push({ id: docSnap.id, ...docSnap.data() });
    });

    return { success: true, transactions };
  } catch (error) {
    console.error('Error loading tax vault transactions:', error);
    return { success: false, error: error.message, transactions: [] };
  }
};

export const addTaxVaultTransaction = async (userId, transactionData) => {
  try {
    const txRef = collection(db, 'users', userId, 'taxVaultTransactions');
    const txDoc = await addDoc(txRef, {
      ...transactionData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    return { success: true, id: txDoc.id };
  } catch (error) {
    console.error('Error saving tax vault transaction:', error);
    return { success: false, error: error.message };
  }
};

export const syncTaxVaultFromIncome = async (userId, incomeEntries = [], reservePercent = 25) => {
  try {
    const txRef = collection(db, 'users', userId, 'taxVaultTransactions');
    const existingSnap = await getDocs(txRef);
    const existingIncomeKeys = new Set();

    existingSnap.forEach((docSnap) => {
      const row = docSnap.data() || {};
      if (row.sourceInvoiceId) existingIncomeKeys.add(String(row.sourceInvoiceId));
      if (row.sourceIncomeId) existingIncomeKeys.add(String(row.sourceIncomeId));
    });

    let createdCount = 0;
    let createdAmount = 0;

    for (const entry of (incomeEntries || [])) {
      const incomeAmount = Number(entry.amount || 0);
      if (!(incomeAmount > 0)) continue;

      const incomeKey = String(
        entry.sourceInvoiceId ||
        entry.id ||
        `${entry.source || 'income'}-${incomeAmount}-${normalizeDate(entry.date)}`
      );

      if (existingIncomeKeys.has(incomeKey)) continue;

      const reserveAmount = Number((incomeAmount * (reservePercent / 100)).toFixed(2));
      const sourceIsInvoice = String(entry.source || '').toLowerCase().includes('invoice') || Boolean(entry.sourceInvoiceId);
      await addDoc(txRef, {
        type: 'deposit',
        amount: reserveAmount,
        incomeAmount,
        reservePercent,
        source: sourceIsInvoice ? 'invoice_auto' : 'income_auto',
        sourceIncomeId: incomeKey,
        sourceInvoiceId: sourceIsInvoice ? String(entry.sourceInvoiceId || entry.id || '') : '',
        date: normalizeDate(entry.date),
        note: sourceIsInvoice
          ? `Auto reserve from invoice ${entry.invoiceNumber || entry.sourceInvoiceId || entry.id || ''}`
          : `Auto reserve from income ${entry.source || incomeKey}`,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      createdCount += 1;
      createdAmount += reserveAmount;
      existingIncomeKeys.add(incomeKey);
    }

    return { success: true, createdCount, createdAmount };
  } catch (error) {
    console.error('Error syncing tax vault from income:', error);
    return { success: false, error: error.message, createdCount: 0, createdAmount: 0 };
  }
};

export const syncTaxVaultFromPaidInvoices = async (userId, invoices = [], reservePercent = 25) => {
  const incomeEntries = (invoices || [])
    .filter((inv) => inv.status === 'paid')
    .map((inv) => ({
      id: inv.id,
      source: 'invoice',
      sourceInvoiceId: String(inv.id || inv.invoiceNumber || ''),
      invoiceNumber: inv.invoiceNumber,
      amount: Number(inv.total || 0),
      date: inv.paidDate || inv.date
    }));

  return syncTaxVaultFromIncome(userId, incomeEntries, reservePercent);
};

// ==================== SUGGESTED KEYWORDS (for future version updates) ====================

export const saveSuggestedKeyword = async (userId, keywordData) => {
  try {
    const keywordId = keywordData.id || Date.now().toString();
    const keywordRef = doc(db, 'suggested_keywords', keywordId);
    
    await setDoc(keywordRef, {
      ...keywordData,
      userId,
      status: keywordData.status || 'pending', // pending | approved | rejected
      createdAt: serverTimestamp()
    });
    
    return { success: true, id: keywordId };
  } catch (error) {
    console.error('Error saving suggested keyword:', error);
    return { success: false, error: error.message };
  }
};

export const loadSuggestedKeywords = async (userId) => {
  try {
    if (!userId) {
      console.warn('loadSuggestedKeywords called without userId');
      return { success: true, keywords: [] };
    }
    
    const keywordsRef = collection(db, 'suggested_keywords');
    const q = query(
      keywordsRef,
      where('userId', '==', userId),
      orderBy('createdAt', 'desc')
    );
    const querySnapshot = await getDocs(q);
    
    const keywords = [];
    querySnapshot.forEach((docSnap) => {
      keywords.push({ id: docSnap.id, ...docSnap.data() });
    });
    
    return { success: true, keywords };
  } catch (error) {
    console.error('Error loading suggested keywords:', error);
    return { success: false, error: error.message, keywords: [] };
  }
};
// ==================== ENHANCED TAX VAULT FUNCTIONS ====================

/**
 * Get Tax Vault analytics and insights
 */
export const getTaxVaultAnalytics = async (userId) => {
  try {
    const [settingsResult, txResult] = await Promise.all([
      loadTaxVaultSettings(userId),
      loadTaxVaultTransactions(userId)
    ]);

    if (!settingsResult.success || !txResult.success) {
      return { success: false, error: 'Failed to load data' };
    }

    const settings = settingsResult.settings;
    const transactions = txResult.transactions;

    const deposits = transactions.filter(tx => tx.type === 'deposit');
    const withdrawals = transactions.filter(tx => tx.type === 'withdrawal');
    
    const totalDeposits = deposits.reduce((sum, tx) => sum + Number(tx.amount || 0), 0);
    const totalWithdrawals = withdrawals.reduce((sum, tx) => sum + Number(tx.amount || 0), 0);
    const currentBalance = totalDeposits - totalWithdrawals;

    const monthlyTrends = {};
    const now = new Date();
    
    for (let i = 0; i < 12; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      monthlyTrends[monthKey] = { deposits: 0, withdrawals: 0, net: 0 };
    }

    transactions.forEach(tx => {
      const txDate = new Date(tx.date);
      const monthKey = `${txDate.getFullYear()}-${String(txDate.getMonth() + 1).padStart(2, '0')}`;
      
      if (monthlyTrends[monthKey]) {
        const amount = Number(tx.amount || 0);
        if (tx.type === 'deposit') {
          monthlyTrends[monthKey].deposits += amount;
        } else {
          monthlyTrends[monthKey].withdrawals += amount;
        }
        monthlyTrends[monthKey].net = monthlyTrends[monthKey].deposits - monthlyTrends[monthKey].withdrawals;
      }
    });

    const withdrawalsByPurpose = {};
    withdrawals.forEach(tx => {
      const purpose = tx.purpose || 'other';
      if (!withdrawalsByPurpose[purpose]) {
        withdrawalsByPurpose[purpose] = { count: 0, total: 0 };
      }
      withdrawalsByPurpose[purpose].count += 1;
      withdrawalsByPurpose[purpose].total += Number(tx.amount || 0);
    });

    const avgDeposit = deposits.length > 0 ? totalDeposits / deposits.length : 0;

    return {
      success: true,
      analytics: {
        currentBalance,
        totalDeposits,
        totalWithdrawals,
        depositCount: deposits.length,
        withdrawalCount: withdrawals.length,
        avgDeposit,
        monthlyTrends,
        withdrawalsByPurpose,
        reservePercent: settings.reservePercent,
        cushionPercent: settings.cushionPercent
      }
    };
  } catch (error) {
    console.error('Error getting tax vault analytics:', error);
    return { success: false, error: error.message, analytics: {} };
  }
};

/**
 * Update tax vault transaction
 */
export const updateTaxVaultTransaction = async (userId, transactionId, updates) => {
  try {
    const txRef = doc(db, 'users', userId, 'taxVaultTransactions', transactionId);
    await updateDoc(txRef, {
      ...updates,
      updatedAt: serverTimestamp()
    });
    
    return { success: true };
  } catch (error) {
    console.error('Error updating tax vault transaction:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Delete tax vault transaction
 */
export const deleteTaxVaultTransaction = async (userId, transactionId) => {
  try {
    const txRef = doc(db, 'users', userId, 'taxVaultTransactions', transactionId);
    await deleteDoc(txRef);
    
    return { success: true };
  } catch (error) {
    console.error('Error deleting tax vault transaction:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get quarterly tax estimates
 */
export const getQuarterlyTaxEstimates = async (userId, year) => {
  try {
    const txRef = collection(db, 'users', userId, 'taxVaultTransactions');
    const querySnapshot = await getDocs(txRef);

    const targetYear = year || new Date().getFullYear();
    const quarters = {
      Q1: { months: [0, 1, 2], deposits: 0, withdrawals: 0, income: 0 },
      Q2: { months: [3, 4, 5], deposits: 0, withdrawals: 0, income: 0 },
      Q3: { months: [6, 7, 8], deposits: 0, withdrawals: 0, income: 0 },
      Q4: { months: [9, 10, 11], deposits: 0, withdrawals: 0, income: 0 }
    };

    querySnapshot.forEach((docSnap) => {
      const tx = docSnap.data();
      const txDate = new Date(tx.date);
      
      if (txDate.getFullYear() !== targetYear) return;

      const month = txDate.getMonth();
      const amount = Number(tx.amount || 0);
      const income = Number(tx.incomeAmount || 0);

      Object.entries(quarters).forEach(([quarter, data]) => {
        if (data.months.includes(month)) {
          if (tx.type === 'deposit') {
            quarters[quarter].deposits += amount;
            quarters[quarter].income += income;
          } else {
            quarters[quarter].withdrawals += amount;
          }
        }
      });
    });

    const estimates = {};
    Object.entries(quarters).forEach(([quarter, data]) => {
      estimates[quarter] = {
        totalIncome: data.income,
        reserves: data.deposits,
        paid: data.withdrawals,
        remaining: data.deposits - data.withdrawals,
        estimatedTax: data.income * 0.25,
        percentPaid: data.deposits > 0 ? (data.withdrawals / data.deposits) * 100 : 0
      };
    });

    return { success: true, year: targetYear, estimates };
  } catch (error) {
    console.error('Error getting quarterly estimates:', error);
    return { success: false, error: error.message, estimates: {} };
  }
};

// ==================== QUANTRA CHAT HISTORY ====================

export const saveQuantraChatHistory = async (userId, messages) => {
  try {
    const trimmed = (messages || []).slice(-100).map((msg) => ({
      role: msg.role,
      content: msg.content,
      timestamp: msg.timestamp instanceof Date ? msg.timestamp.toISOString() : msg.timestamp,
    }));
    const docRef = doc(db, 'users', userId, 'quantra', 'chat_history');
    await setDoc(docRef, {
      messages: trimmed,
      lastUpdated: new Date().toISOString(),
      messageCount: trimmed.length,
    });
    return { success: true };
  } catch (error) {
    console.error('Save chat history error:', error);
    return { success: false, error: error.message };
  }
};

export const loadQuantraChatHistory = async (userId) => {
  try {
    const docRef = doc(db, 'users', userId, 'quantra', 'chat_history');
    const snap = await getDoc(docRef);
    if (snap.exists()) {
      const data = snap.data();
      return {
        success: true,
        messages: (data.messages || []).map((msg) => ({
          ...msg,
          timestamp: new Date(msg.timestamp),
        })),
      };
    }
    return { success: true, messages: [] };
  } catch (error) {
    console.error('Load chat history error:', error);
    return { success: false, messages: [] };
  }
};

export const clearQuantraChatHistory = async (userId) => {
  try {
    const docRef = doc(db, 'users', userId, 'quantra', 'chat_history');
    await deleteDoc(docRef);
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// ==================== PUSH NOTIFICATIONS (FCM) ====================
export const requestNotificationPermission = async (userId) => {
  try {
    // Native iOS (Capacitor WKWebView): browser Notification API is unsupported and
    // calling Notification.requestPermission() will throw. Return silently.
    if (window.Capacitor?.isNativePlatform?.()) {
      return { success: false, reason: 'unsupported' };
    }

    if (typeof window === 'undefined' || typeof Notification === 'undefined') {
      return { success: false, reason: 'unsupported' };
    }

    // Service workers are required for FCM push tokens — skip gracefully if unavailable
    if (!('serviceWorker' in navigator)) {
      return { success: false, reason: 'unsupported' };
    }

    const messaging = await getMessagingInstance();
    if (!messaging) return { success: false, reason: 'unsupported' };

    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return { success: false, reason: 'denied' };

    // Service worker registration only runs on web (native iOS already returned above)
    try {
      await navigator.serviceWorker.register('/firebase-messaging-sw.js');
    } catch (swErr) {
      console.warn('[FCM] Service worker registration failed:', swErr?.message);
      // Continue — existing SW registration may still work
    }

    const vapidKey = process.env.REACT_APP_FIREBASE_VAPID_KEY;
    if (!vapidKey) return { success: false, reason: 'missing_vapid_key' };

    const { getToken } = await import('firebase/messaging');
    const token = await getToken(messaging, { vapidKey });
    if (!token) return { success: false, reason: 'token_unavailable' };

    const docRef = doc(db, 'users', userId, 'settings', 'notifications');
    await setDoc(docRef, {
      fcmToken: token,
      enabled: true,
      updatedAt: new Date().toISOString(),
    }, { merge: true });

    return { success: true, token };
  } catch (error) {
    console.error('FCM permission error:', error);
    return { success: false, error: error.message };
  }
};

export const onForegroundMessage = (callback) => {
  // async — returns a cleanup no-op immediately, attaches listener when messaging loads
  let unsub = () => {};
  getMessagingInstance().then(async (messaging) => {
    if (!messaging) return;
    const { onMessage } = await import('firebase/messaging');
    unsub = onMessage(messaging, (payload) => callback(payload));
  });
  return () => unsub();
};

// ── RBAC: User Profile ────────────────────────────────────────────────────────
export const loadUserProfile = async (userId) => {
  try {
    const snap = await getDoc(doc(db, 'users', userId, 'profile', 'data'));
    return snap.exists() ? snap.data() : {};
  } catch (e) {
    return {};
  }
};

export const saveUserProfile = async (userId, fields) => {
  try {
    await setDoc(doc(db, 'users', userId, 'profile', 'data'), fields, { merge: true });
    return { success: true };
  } catch (e) {
    return { success: false };
  }
};

// ==================== STRIPE / BILLING FUNCTIONS ====================

export const createCheckoutSession = async (priceKey) => {
  try {
    const fn = httpsCallable(functions, 'createCheckoutSession');
    const result = await fn({ priceKey, origin: window.location.origin });
    return result.data;
  } catch (error) {
    console.error('Checkout error:', error);
    return { error: error.message };
  }
};

export const createBillingPortal = async () => {
  try {
    const fn = httpsCallable(functions, 'createBillingPortal');
    const result = await fn({ origin: window.location.origin });
    return result.data;
  } catch (error) {
    console.error('Billing portal error:', error);
    return { error: error.message };
  }
};

export const deductPaygCredits = async (action) => {
  try {
    const fn = httpsCallable(functions, 'deductPaygCredits');
    const result = await fn({ action });
    return result.data;
  } catch (error) {
    console.error('Credit deduction error:', error);
    return { allowed: false, reason: 'error', error: error.message };
  }
};

export const incrementUsage = async (userId, metric) => {
  const day = new Date().toISOString().slice(0, 10);
  const ref = doc(db, 'users', userId, 'usage', day);
  const snap = await getDoc(ref);
  const current = snap.exists() ? snap.data()[metric] || 0 : 0;
  await setDoc(ref, { [metric]: current + 1 }, { merge: true });
  return current + 1;
};

export const getUsage = async (userId) => {
  const day = new Date().toISOString().slice(0, 10);
  const ref = doc(db, 'users', userId, 'usage', day);
  const snap = await getDoc(ref);
  return snap.exists() ? snap.data() : {};
};

// ==================== SOCIAL ACCOUNT FUNCTIONS ====================

export const saveSocialAccount = async (userId, platform, accountData) => {
  try {
    const id = `${platform}_${accountData.accountId || Date.now()}`;
    const ref = doc(db, 'users', userId, 'socialAccounts', id);
    await setDoc(ref, {
      platform,
      accountId: accountData.accountId || '',
      accountName: accountData.accountName || '',
      accessToken: accountData.accessToken || '',
      refreshToken: accountData.refreshToken || '',
      tokenExpiry: accountData.tokenExpiry || null,
      profileUrl: accountData.profileUrl || '',
      profileImage: accountData.profileImage || '',
      connected: true,
      connectedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    }, { merge: true });
    return { success: true, id };
  } catch (error) {
    console.error('Error saving social account:', error);
    return { success: false, error: error.message };
  }
};

export const loadSocialAccounts = async (userId) => {
  try {
    const ref = collection(db, 'users', userId, 'socialAccounts');
    const snap = await getDocs(ref);
    const accounts = [];
    const disabledPlatforms = new Set(['instagram', 'facebook']);
    snap.forEach((docSnap) => {
      const data = docSnap.data();
      if (!data.connected || disabledPlatforms.has(data.platform)) return;
      accounts.push({ id: docSnap.id, ...data });
    });
    return { success: true, accounts };
  } catch (error) {
    console.error('Error loading social accounts:', error);
    return { success: false, accounts: [] };
  }
};

export const disconnectSocialAccount = async (userId, accountId) => {
  try {
    const ref = doc(db, 'users', userId, 'socialAccounts', accountId);
    await updateDoc(ref, {
      connected: false,
      accessToken: '',
      refreshToken: '',
      updatedAt: serverTimestamp(),
    });
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const startYouTubeAuth = async () => {
  try {
    const fn = httpsCallable(functions, 'youtubeStartAuth');
    const result = await fn({});
    return result.data;
  } catch (error) {
    console.error('YouTube auth error:', error);
    return { error: error.message };
  }
};

export const uploadVideoToYouTube = async ({ videoUrl, title, description, tags, privacyStatus }) => {
  try {
    const fn = httpsCallable(functions, 'youtubeUploadVideo');
    const result = await fn({ videoUrl, title, description, tags, privacyStatus });
    return result.data;
  } catch (error) {
    console.error('YouTube upload error:', error);
    return { success: false, error: error.message };
  }
};

export const disconnectYouTube = async () => {
  try {
    const fn = httpsCallable(functions, 'youtubeDisconnect');
    const result = await fn({});
    return result.data;
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// ==================== SCHEDULED POSTS ====================

export const saveScheduledPost = async (userId, postData) => {
  try {
    const ref = collection(db, 'users', userId, 'scheduledPosts');
    const docRef = await addDoc(ref, {
      ...postData,
      status: postData.scheduleAt ? 'scheduled' : 'queued',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    return { success: true, id: docRef.id };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const loadScheduledPosts = async (userId, maxRows = 50) => {
  try {
    const ref = collection(db, 'users', userId, 'scheduledPosts');
    const q = query(ref, orderBy('createdAt', 'desc'));
    const snap = await getDocs(q);
    const posts = [];
    snap.forEach((docSnap) => posts.push({ id: docSnap.id, ...docSnap.data() }));
    return { success: true, posts: posts.slice(0, maxRows) };
  } catch (error) {
    return { success: false, posts: [] };
  }
};

export const deleteScheduledPost = async (userId, postId) => {
  try {
    await deleteDoc(doc(db, 'users', userId, 'scheduledPosts', postId));
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const publishSocialPost = async (postId) => {
  try {
    const fn = httpsCallable(functions, 'publishSocialPost');
    const result = await fn({ postId });
    return result.data;
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export default app;

// ══════════════════════════════════════════════════════════════════════════════
// COLLABORATION — Workspaces, Invites, Accountant Access, Invoice Share Links
// ══════════════════════════════════════════════════════════════════════════════

// ── Workspace management ───────────────────────────────────────────────────────

/** Create a new workspace owned by `userId`. Returns { success, workspaceId }. */
export const createWorkspace = async (userId, name) => {
  try {
    const ref = doc(collection(db, 'workspaces'));
    await setDoc(ref, {
      name: name.trim(),
      ownerId: userId,
      createdAt: serverTimestamp(),
    });
    return { success: true, workspaceId: ref.id };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/** Load the workspace owned by `userId` (one workspace per owner). */
export const loadMyWorkspace = async (userId) => {
  try {
    const q = query(collection(db, 'workspaces'), where('ownerId', '==', userId));
    const snap = await getDocs(q);
    if (snap.empty) return { success: true, workspace: null };
    const docSnap = snap.docs[0];
    return { success: true, workspace: { id: docSnap.id, ...docSnap.data() } };
  } catch (error) {
    return { success: false, workspace: null, error: error.message };
  }
};

/** Load all workspaces the current user is a member of (not owner). */
export const loadJoinedWorkspaces = async (userId) => {
  try {
    const q = query(
      collection(db, 'workspaceMemberships'),
      where('memberId', '==', userId),
      where('status', '==', 'active')
    );
    const snap = await getDocs(q);
    const workspaces = [];
    for (const d of snap.docs) {
      const data = d.data();
      const wsSnap = await getDoc(doc(db, 'workspaces', data.workspaceId));
      if (wsSnap.exists()) {
        workspaces.push({ id: wsSnap.id, ...wsSnap.data(), memberRole: data.role, membershipId: d.id });
      }
    }
    return { success: true, workspaces };
  } catch (error) {
    return { success: false, workspaces: [], error: error.message };
  }
};

/** Load all active members of a workspace (by ownerId). */
export const loadWorkspaceMembers = async (ownerId) => {
  try {
    const q = query(collection(db, 'workspaceMemberships'), where('ownerId', '==', ownerId));
    const snap = await getDocs(q);
    const members = [];
    snap.forEach((d) => members.push({ id: d.id, ...d.data() }));
    return { success: true, members };
  } catch (error) {
    return { success: false, members: [], error: error.message };
  }
};

/** Remove a member from the owner's workspace. */
export const removeWorkspaceMember = async (membershipId) => {
  try {
    await deleteDoc(doc(db, 'workspaceMemberships', membershipId));
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// ── Invitations ────────────────────────────────────────────────────────────────

/** Generate a random invite token. */
const genToken = () => {
  const arr = new Uint8Array(18);
  window.crypto.getRandomValues(arr);
  return Array.from(arr, (b) => b.toString(16).padStart(2, '0')).join('');
};

/**
 * Create a workspace invite link.
 * @param {string} invitedBy  - uid of the owner sending the invite
 * @param {string} workspaceId
 * @param {string} ownerId    - same as invitedBy for now (owner's data uid)
 * @param {string} email      - invitee email (informational only, not enforced)
 * @param {'admin'|'member'|'accountant'} role
 * Returns { success, token, inviteUrl }
 */
export const createWorkspaceInvite = async (invitedBy, workspaceId, ownerId, email, role) => {
  try {
    const token = genToken();
    await setDoc(doc(db, 'workspaceInvites', token), {
      workspaceId,
      ownerId,
      email: email.toLowerCase().trim(),
      role,
      invitedBy,
      status: 'pending',
      createdAt: serverTimestamp(),
      expiresAt: Timestamp.fromDate(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)), // 7 days
    });
    const inviteUrl = `${window.location.origin}${window.location.pathname}?invite=${token}`;
    return { success: true, token, inviteUrl };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/** Read an invite by token (public). */
export const getWorkspaceInvite = async (token) => {
  try {
    const snap = await getDoc(doc(db, 'workspaceInvites', token));
    if (!snap.exists()) return { success: false, error: 'Invite not found or expired.' };
    const data = snap.data();
    if (data.status !== 'pending') return { success: false, error: 'This invite has already been used.' };
    if (data.expiresAt && data.expiresAt.toDate() < new Date()) {
      return { success: false, error: 'This invite link has expired.' };
    }
    return { success: true, invite: { token, ...data } };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * Accept an invite — creates the workspaceMembership doc and marks invite used.
 * Must be called while the invitee is signed in.
 */
export const acceptWorkspaceInvite = async (token, memberId, memberEmail, memberName) => {
  try {
    const inviteRes = await getWorkspaceInvite(token);
    if (!inviteRes.success) return inviteRes;
    const { invite } = inviteRes;

    const membershipId = `${memberId}_${invite.ownerId}`;
    await setDoc(doc(db, 'workspaceMemberships', membershipId), {
      memberId,
      memberEmail: memberEmail || invite.email,
      memberName: memberName || memberEmail || invite.email,
      ownerId: invite.ownerId,
      workspaceId: invite.workspaceId,
      role: invite.role,
      status: 'active',
      joinedAt: serverTimestamp(),
    });

    // Mark invite consumed
    await updateDoc(doc(db, 'workspaceInvites', token), { status: 'accepted' });

    return { success: true, ownerId: invite.ownerId, role: invite.role };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/** Delete a pending invite (revoke). */
export const revokeWorkspaceInvite = async (token) => {
  try {
    await deleteDoc(doc(db, 'workspaceInvites', token));
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/** Load all pending invites sent by `invitedBy`. */
export const loadPendingInvites = async (invitedBy) => {
  try {
    const q = query(
      collection(db, 'workspaceInvites'),
      where('invitedBy', '==', invitedBy),
      where('status', '==', 'pending')
    );
    const snap = await getDocs(q);
    const invites = [];
    snap.forEach((d) => invites.push({ token: d.id, ...d.data() }));
    return { success: true, invites };
  } catch (error) {
    return { success: false, invites: [], error: error.message };
  }
};

// ── Invoice share links ────────────────────────────────────────────────────────

/**
 * Create a public share link for an invoice.
 * Stores a snapshot of the invoice so the link works without auth.
 * Returns { success, token, shareUrl }
 */
export const createInvoiceLink = async (userId, invoiceId, invoiceSnapshot) => {
  try {
    const token = genToken();
    await setDoc(doc(db, 'invoiceLinks', token), {
      invoiceId,
      ownerId: userId,
      invoice: invoiceSnapshot,
      createdAt: serverTimestamp(),
      views: 0,
    });
    const shareUrl = `${window.location.origin}${window.location.pathname}?invoice=${token}`;
    return { success: true, token, shareUrl };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/** Load an invoice by share token (public, no auth). */
export const getInvoiceByToken = async (token) => {
  try {
    const snap = await getDoc(doc(db, 'invoiceLinks', token));
    if (!snap.exists()) return { success: false, error: 'Invoice link not found.' };
    return { success: true, data: snap.data() };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/** Revoke (delete) an invoice share link. */
export const revokeInvoiceLink = async (token) => {
  try {
    await deleteDoc(doc(db, 'invoiceLinks', token));
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/** Load all active invoice share links for a user. */
export const loadInvoiceLinks = async (userId) => {
  try {
    const q = query(collection(db, 'invoiceLinks'), where('ownerId', '==', userId));
    const snap = await getDocs(q);
    const links = [];
    snap.forEach((d) => links.push({ token: d.id, ...d.data() }));
    return { success: true, links };
  } catch (error) {
    return { success: false, links: [], error: error.message };
  }
};

// ── Waitlist ──────────────────────────────────────────────────────────────────
export const signInWithAppleCredential = async (identityToken, rawNonce) => {
  try {
    const provider = new OAuthProvider('apple.com');
    const credential = provider.credential({ idToken: identityToken, rawNonce });
    const result = await signInWithCredential(auth, credential);
    return { success: true, user: result.user };
  } catch (error) {
    return { success: false, error: error.message, code: error.code };
  }
};

// Web only — signInWithPopup is unreliable inside Capacitor's native WebView
// (no real browser context for the OAuth redirect to land back into). Native
// Google Sign-In needs a dedicated plugin (e.g. @codetrix-studio/capacitor-google-auth)
// plus OAuth client IDs configured per-platform; not wired up here. Callers
// should check Capacitor.isNativePlatform() before calling this.
export const signInWithGoogle = async () => {
  try {
    const provider = new GoogleAuthProvider();
    const result = await signInWithPopup(auth, provider, browserPopupRedirectResolver);
    return { success: true, user: result.user };
  } catch (error) {
    return { success: false, error: error.message, code: error.code };
  }
};

export const saveWaitlistEmail = async (email) => {
  try {
    if (!email || !email.includes('@')) return { success: false, error: 'Invalid email' };

    // Use email as doc ID so duplicates are silently ignored
    const id = email.toLowerCase().trim().replace(/[^a-z0-9@._-]/g, '_');
    await setDoc(doc(db, 'waitlist', id), {
      email: email.toLowerCase().trim(),
      source: 'landing-page',
      joinedAt: serverTimestamp(),
      plan: 'early-access',          // 1 month free Pro on launch
      notified: false,
    }, { merge: true });             // merge:true means duplicate emails just update timestamp

    return { success: true };
  } catch (error) {
    console.error('saveWaitlistEmail error:', error);
    return { success: false, error: error.message };
  }
};
